import { RefObject, useEffect } from 'react';

import { isAllowedOrigin, readReportedHeight, shouldApplyHeight } from '@utils/iframeResize';

import debounce from '../application/javascripts/debounce';

const IFRAME_RESIZE = 'iframe.resize';

/**
 * Auto-height a single iframe from allowlisted postMessage height reports.
 * Used by SelfHostedWrapper so /charts/:slug and Deep Research work without
 * relying on the page-level useIframeResizeMessager hook.
 */
const useIframeAutoHeight = (
  iframeRef: RefObject<HTMLIFrameElement | null>,
  expectedOrigin: string | null
) => {
  useEffect(() => {
    if (!expectedOrigin || !isAllowedOrigin(expectedOrigin)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const height = readReportedHeight(event.data);
      if (height === null || !iframeRef.current) return;

      const currentHeight = Number.parseInt(iframeRef.current.height, 10) || 0;
      if (!shouldApplyHeight(currentHeight, height)) return;

      iframeRef.current.height = String(height);
    };

    const poke = () => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !iframe.src) return;
      try {
        iframe.contentWindow.postMessage({ context: IFRAME_RESIZE }, expectedOrigin);
      } catch {
        // Invalid src — skip
      }
    };

    const debouncedPoke = debounce(poke, 200);

    window.addEventListener('message', onMessage);
    window.addEventListener('resize', debouncedPoke);
    poke();

    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('resize', debouncedPoke);
    };
  }, [iframeRef, expectedOrigin]);
};

export default useIframeAutoHeight;
